import React, { FC, useEffect, useState } from "react";
import { gql, useMutation } from "@apollo/client";
import { AmountToPay } from "utils/insurance/getAmountToPay";
import { getShortestLivingDiscount } from "utils/insurance/getShortestLivingDiscount";
import { YEARLY_BILLING_INTERVAL } from "utils/insurance";
import { useWebTranslation } from "translations";
import { formatCurrency, formatNumber } from "utils/utils";
import InsuranceInfoCard from "components-shared/InsuranceInfoCard";
import Gutter from "ui-kit/Gutter";
import { FlexCol, FlexRow } from "ui-kit/Layout/Flex";
import { FormField } from "components-shared/Form";
import TextInput from "ui-kit/TextInput/TextInput";
import Icon from "ui-kit/Icon";
import Colors from "ui-kit/colors";
import { Button } from "ui-kit/Button";
import { Typography } from "ui-kit/Typography";
import { CampaignCode } from "../steps/types";
import { logEvent } from "analytics";
import {
  ValidateEurobonusMemberId,
  ValidateEurobonusMemberIdVariables,
} from "../../../../__generated__/ValidateEurobonusMemberId";
import { ValidateLunarMemberId } from "../../../../__generated__/ValidateLunarMemberId";
import { Container } from "ui-kit/Layout";

type Status = "applied" | "invalid" | "none";

type DiscountBlockProps = {
  totalMsg?: string;
  perMonthMsg: string;
  title: string;
  placeholder: string;
  status: Status;
  removeBtn: string;
  errorMsg: string;
  onSubmit: (code: string) => void;
  inProgress?: boolean;
  code: string;
  setCode: (code: string) => void;
  annualSaving: number;
  monthlySaving: number;
  maxInvoicesSpread?: number;
  note?: string;
  isPoints?: boolean;
  billingInterval: number;
};

const DiscountBlock: FC<DiscountBlockProps> = ({
  isPoints,
  totalMsg,
  perMonthMsg,
  removeBtn,
  errorMsg,
  title,
  placeholder,
  status,
  onSubmit,
  inProgress,
  code,
  setCode,
  annualSaving,
  monthlySaving,
  maxInvoicesSpread,
  note,
  billingInterval,
}) => {
  const { t } = useWebTranslation();
  const formatDiscount = (n: number): string => {
    if (isPoints) {
      return `${formatNumber(Math.round(n))} ${t("global.points")}`;
    }

    return formatCurrency(n);
  };

  return (
    <InsuranceInfoCard
      shadowStyle="none"
      title={title}
      bodyStyle={{ padding: 0 }}
      headerStyle={{ padding: 0 }}
    >
      <Gutter size={0.5} />
      {(!code || status === "none") && (
        <FlexRow justifyContent="space-between" alignItems="center">
          <FormField style={{ flex: 1 }}>
            <TextInput
              placeHolder={placeholder}
              value={code}
              onChangeText={(text) => setCode(text.trim())}
              style={{
                border: "none",
              }}
            />
          </FormField>
          <Gutter size={2} horizontal />
          <Button
            variant="custom"
            color="blue100"
            textColor="blue300"
            loading={inProgress}
            onPress={() => onSubmit(code)}
            title={t("global.activate")}
            disabled={!code}
            style={{ flexBasis: "content" }}
          />
        </FlexRow>
      )}
      {code && status === "applied" && (
        <FlexCol>
          <FlexRow justifyContent="space-between" alignItems="center">
            <FormField style={{ position: "relative", flex: 1 }}>
              <TextInput
                disabled
                placeHolder={placeholder}
                style={{ border: "none" }}
                value={code}
                onChangeText={(text) => setCode(text)}
              />

              <div style={{ position: "absolute", top: 8, right: 8 }}>
                <Icon
                  color={Colors.green300}
                  size={20}
                  name="checkmark_circle"
                />
              </div>
            </FormField>
            <Gutter size={2} horizontal />
            <Button
              variant="custom"
              color="errorBg"
              textColor="error"
              loading={inProgress}
              onPress={() => setCode("")}
              title={t(removeBtn)}
              disabled={!code}
              style={{ flexBasis: "content" }}
            />
          </FlexRow>
          {annualSaving > 0 && (
            <div>
              <Gutter size={2} />
              {totalMsg && (
                <>
                  <FlexRow justifyContent="space-between">
                    <Typography variant="body2" color="grey400">
                      {totalMsg}
                    </Typography>
                    <Typography variant="body2" color="green300">
                      {formatDiscount(annualSaving)}
                    </Typography>
                  </FlexRow>
                  <Gutter size={1} />
                </>
              )}
              <FlexRow justifyContent="space-between">
                <Typography variant="body2" color="grey400">
                  {billingInterval === YEARLY_BILLING_INTERVAL
                    ? t("discounts.valid_for_first_year")
                    : maxInvoicesSpread
                    ? maxInvoicesSpread === 1
                      ? t("discount_code.one_month")
                      : t("discount_code.per_x_months", {
                          months: maxInvoicesSpread,
                        })
                    : perMonthMsg}
                </Typography>
                <Typography variant="body2" color="green300">
                  <strong>
                    {formatDiscount(
                      billingInterval === YEARLY_BILLING_INTERVAL
                        ? annualSaving
                        : monthlySaving
                    )}
                  </strong>
                </Typography>
              </FlexRow>
            </div>
          )}
        </FlexCol>
      )}
      {code && status === "invalid" && (
        <FlexCol>
          <FlexRow justifyContent="space-between" alignItems="center">
            <FormField
              style={{
                position: "relative",
                borderColor: Colors.error,
                flex: 1,
              }}
            >
              <TextInput
                style={{ border: "none" }}
                value={code}
                onChangeText={(text) => setCode(text)}
              />
            </FormField>
            <Gutter size={2} horizontal />
            <Button
              variant="custom"
              color="errorBg"
              textColor="error"
              loading={inProgress}
              onPress={() => setCode("")}
              title={t(removeBtn)}
              disabled={!code}
              style={{ flexBasis: "content" }}
            />
          </FlexRow>
          <Gutter size={1} />
          <Typography color="error">{errorMsg}</Typography>
        </FlexCol>
      )}
      {note && (
        <>
          <Gutter size={2} />
          <Typography variant="body3" color="grey500">
            {note}
          </Typography>
        </>
      )}
    </InsuranceInfoCard>
  );
};

type DiscountCodeProps = {
  amountToPay: AmountToPay;
  fees?: { maxInvoicesSpread: number | null }[] | null;
  campaignCode?: CampaignCode | null;
  discount: {
    inProgress?: boolean;
    onSubmit: (code: string) => void;
    appliedCode: string;
  };
  eurobonus?: {
    onSubmit: (code: string | null, isValid: boolean) => void;
    memberId: string | null;
    isValid: boolean;
  };
  lunar?: {
    onSubmit: (code: string | null, isValid: boolean) => void;
    memberId: string | null;
    isValid: boolean;
  };
  billingInterval: number;
};

const DiscountCode: FC<DiscountCodeProps> = ({
  campaignCode,
  eurobonus,
  lunar,
  discount,
  amountToPay,
  fees,
  billingInterval,
}) => {
  const { t } = useWebTranslation();
  const [expanded, setExpanded] = useState<boolean>(
    !!(
      discount.appliedCode ||
      campaignCode ||
      eurobonus?.memberId ||
      lunar?.memberId
    )
  );
  const [discountCode, setDiscountCode] = useState<string>("");
  const [discountStatus, setDiscountStatus] = useState<Status>("none");

  useEffect(() => {
    setDiscountCode(discount.appliedCode);
  }, [discount.appliedCode]);

  useEffect(() => {
    if (discount.appliedCode && fees?.length) {
      logEvent("DISCOUNT_APPLIED", { discountCode });
      setDiscountStatus("applied");
      return;
    }
    if (discount.appliedCode && !fees?.length) {
      setDiscountStatus("invalid");
      return;
    }
    setDiscountStatus("none");
  }, [fees]);

  const annualSaving =
    (amountToPay.fees.reduce((acc, fee) => acc + fee.total, 0) / 100) * -1;
  const shortestFee = getShortestLivingDiscount(amountToPay.fees);
  const monthlySaving = shortestFee
    ? shortestFee.total / shortestFee.maxInvoicesSpread / 100
    : 0;

  const [eurobonusMemberId, setEurobonusMemberId] = useState<string | null>(
    eurobonus?.memberId ?? null
  );
  const [lunarMemberId, setLunarMemberId] = useState<string | null>(
    lunar?.memberId ?? null
  );
  const [eurobonusStatus, setEurobonusStatus] = useState<Status>("none");
  const [lunarStatus, setLunarStatus] = useState<Status>("none");

  const [
    validateEurobonusMemberId,
    { loading: eurobonusValidating, error: eurobonusError },
  ] = useMutation<
    ValidateEurobonusMemberId,
    ValidateEurobonusMemberIdVariables
  >(VALIDATE_EUROBONUS_MEMBER_ID, {
    variables: {
      memberId: eurobonusMemberId,
    },
  });

  useEffect(() => {
    if (!eurobonus?.memberId || (eurobonus.isValid && eurobonusValidating)) {
      setEurobonusStatus("none");
    } else {
      setEurobonusStatus(
        eurobonus.isValid && !eurobonusError ? "applied" : "invalid"
      );
    }
  }, [eurobonus?.isValid, eurobonus?.memberId, eurobonusValidating]);

  const submitEurobonus = async (memberId: string | null) => {
    if (memberId) {
      const id = memberId.replace("EB", "");
      // id should be number
      if (/^\d+$/.test(id)) {
        eurobonus?.onSubmit(id, eurobonus.isValid);
        const { data } = await validateEurobonusMemberId({
          variables: {
            memberId: id,
          },
        });
        const isValid = !id || !!data?.validateEurobonusMemberId;
        eurobonus?.onSubmit(id, isValid);
      } else {
        eurobonus?.onSubmit(id, false);
      }
    } else {
      eurobonus?.onSubmit(null, true);
    }
  };

  const [
    validateLunarMemberId,
    { loading: lunarValidating, error: lunarError },
  ] = useMutation<ValidateLunarMemberId, ValidateEurobonusMemberIdVariables>(
    VALIDATE_LUNAR_MEMBER_ID,
    {
      variables: {
        memberId: lunarMemberId,
      },
    }
  );

  useEffect(() => {
    if (!lunar?.memberId || (lunar.isValid && lunarValidating)) {
      setLunarStatus("none");
    } else {
      setLunarStatus(lunar.isValid && !lunarError ? "applied" : "invalid");
    }
  }, [lunar?.isValid, lunar?.memberId, lunarValidating]);

  const submitLunar = async (memberId: string | null) => {
    if (memberId) {
      // TODO: member id is IBAN starting with DK for now but will change during spring 2023
      if (memberId.startsWith("DK") || memberId.startsWith("971")) {
        lunar?.onSubmit(memberId, lunar?.isValid);
        const { data } = await validateLunarMemberId({
          variables: {
            memberId,
          },
        });
        const isValid = !memberId || !!data?.validateLunarMemberId;
        lunar?.onSubmit(memberId, isValid);
      } else {
        lunar?.onSubmit(memberId, false);
      }
    } else {
      lunar?.onSubmit(null, true);
    }
  };

  return (
    <>
      {!expanded && (
        <Button
          icon={<Icon color={Colors.grey500} name="check-badge" size={20} />}
          title={t("discount_code.add_btn")}
          iconFirst
          testId={"link-button"}
          onPress={() => {
            setExpanded(true);
          }}
          variant="custom"
          textColor="grey400"
        />
      )}
      {expanded && (
        <>
          <Container border>
            <DiscountBlock
              totalMsg={t("discount_code.applied_msg")}
              perMonthMsg={t("discount_code.per_month")}
              removeBtn={t("discount_code.remove_btn")}
              errorMsg={t("discount_code.invalid_msg")}
              title={t("discount_code.title")}
              placeholder={t("discount_code.placeholder")}
              code={discountCode}
              setCode={async (code: string) => {
                setDiscountCode(code.toUpperCase());
                if (code === "") {
                  discount.onSubmit("");
                }
              }}
              inProgress={discount.inProgress}
              onSubmit={discount.onSubmit}
              status={discountStatus}
              annualSaving={annualSaving}
              monthlySaving={monthlySaving}
              maxInvoicesSpread={shortestFee?.maxInvoicesSpread}
              note={t("discount_code.note")}
              billingInterval={billingInterval}
            />

            {campaignCode === CampaignCode.Eurobonus && (
              <>
                <Gutter size={3} />
                <DiscountBlock
                  perMonthMsg={t("eurobonus.member_id.per_month")}
                  removeBtn={t("eurobonus.member_id.remove_btn")}
                  errorMsg={t("eurobonus.member_id.error")}
                  title="Eurobonus"
                  placeholder={t("eurobonus.member_id.placeholder")}
                  code={eurobonusMemberId || ""}
                  setCode={async (code: string) => {
                    setEurobonusMemberId(code);
                    if (code === "") {
                      submitEurobonus(code);
                    }
                  }}
                  inProgress={eurobonusValidating}
                  onSubmit={submitEurobonus}
                  status={eurobonusStatus}
                  annualSaving={Math.floor(amountToPay.new / 100) * 100}
                  monthlySaving={(Math.floor(amountToPay.new / 100) * 100) / 12}
                  note={t("eurobonus.member_id.note")}
                  isPoints
                  billingInterval={billingInterval}
                />
              </>
            )}
            {campaignCode === CampaignCode.Lunar && (
              <>
                <Gutter size={3} />
                <DiscountBlock
                  perMonthMsg={t("lunar.member_id.per_month")}
                  removeBtn={t("lunar.member_id.remove_btn")}
                  errorMsg={t("lunar.member_id.error")}
                  title="Lunar"
                  placeholder={t("lunar.member_id.placeholder")}
                  code={lunarMemberId || ""}
                  setCode={async (code: string) => {
                    setLunarMemberId(code);
                    if (code === "") {
                      submitLunar(code);
                    }
                  }}
                  inProgress={lunarValidating}
                  onSubmit={submitLunar}
                  status={lunarStatus}
                  annualSaving={450 * 12}
                  monthlySaving={450}
                  note={t("lunar.member_id.note")}
                  billingInterval={billingInterval}
                />
              </>
            )}
          </Container>
        </>
      )}
    </>
  );
};

const VALIDATE_EUROBONUS_MEMBER_ID = gql`
  mutation ValidateEurobonusMemberId($memberId: String) {
    validateEurobonusMemberId(memberId: $memberId)
  }
`;

const VALIDATE_LUNAR_MEMBER_ID = gql`
  mutation ValidateLunarMemberId($memberId: String) {
    validateLunarMemberId(memberId: $memberId)
  }
`;

export default DiscountCode;
